import React, { Component } from "react";
import ReactGA from "react-ga";
import { Link } from "react-router-dom";
import BannerImg from "../../assets/images/banner/about-us.png";
import { Helmet } from "react-helmet";
import JsonLd from "../../components/seo/json-ld";

class About extends Component {
  state = {};
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Avani Cimcon Technologies",
        url: "https://www.avanicimcon.com/",
        foundingDate: "1992",
      },
      {
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Lomesh Shah",
        jobTitle: "Co-founder",
        worksFor: {
          "@type": "Organization",
          name: "Avani Cimcon Technologies",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Saurabh Mehta",
        jobTitle: "CEO and Technical Visionary",
        worksFor: {
          "@type": "Organization",
          name: "Avani Cimcon Technologies",
        },
      },
    ];

    return (
      <React.Fragment>
        <Helmet defer={false}>
          <title>About Avani Cimcon | Enterprise Software Engineering Since the Early 1990s</title>
          <meta
            name="keywords"
            content="about Avani Cimcon, enterprise software engineering since the early 1990s, software development company India, software development consulting services, software product development company, IT services, custom software development, travel solutions, insurance solutions, financial services, content management systems, booking engines, legacy modernization, product engineering, enterprise systems"
          />
          <meta
            name="description"
            content="Learn how Avani Cimcon grew from early enterprise systems recorded from 1991 onward into a long-term software engineering partner for modernization, product engineering, and AI-enabled transformation."
          />
          <link rel="canonical" href="https://www.avanicimcon.com/about" />
        </Helmet>
        <JsonLd data={schemas} />

        <div className="page-banner d-flex align-items-center justify-content-center text-white">
          <div className="page-title container position-relative">
            <h1 className="font-weight-bold text-uppercase">About Avani Cimcon</h1>
            <h5>Enterprise software engineering since the early 1990s</h5>
          </div>
          <div className="page-banner-img" style={{ backgroundImage: `url("${BannerImg}")` }}></div>
          <div className="page-banner-overlay"></div>
        </div>

        <div className="inner-page mt-5 mb-5 ">
          <div className="container">
            <div className="text-justify">
              <p>
                Avani Cimcon Technologies began by solving practical enterprise problems: early
                accounting systems, ERP for transport and textile operations, and software that had
                to work reliably in real business environments. That early work became the
                foundation for decades of engineering, offshore delivery, and long-term customer
                relationships.
              </p>
              <p>
                The milestone record begins in 1991 and continues through the company's shift into
                offshore delivery, browser engines, workflow systems, and productized platforms.
                That timeline shows the evolution of a company that has been building enterprise
                software since the early 1990s.
              </p>
              <p>
                As the company matured, the work expanded into browser engines, workflow systems,
                content platforms, booking systems, integration layers, and productized solutions.
                That progression from custom systems to product engineering explains why the team
                today approaches legacy modernization and AI enablement with discipline rather than
                hype.
              </p>
              <p>
                The result is an organization that understands enterprise software as a living
                system. We have delivered for customers across the USA, UK, Asia Pacific,
                Australia, New Zealand, the Middle East and Europe, while keeping the same focus:
                build systems that are useful, durable, and ready for the next technology shift.
              </p>
            </div>

            <div className="shadow-sm p-3 mt-4">
              <h4 className="text-primary mb-3 font-weight-bold">Why this history matters now</h4>
              <p>
                Enterprises modernizing mission-critical software need a partner that understands
                both the original stack and the destination architecture. Avani Cimcon's history
                makes that possible because the company has lived through the technology shifts it
                now helps clients navigate.
              </p>
              <p className="mb-0">
                That is why the modern story connects naturally to{" "}
                <Link to="/it-consulting-services">enterprise IT consulting and modernization</Link>
                , <Link to="/expertise-experience">delivery depth and technical breadth</Link>,
                <Link to="/international-projects">global delivery proof</Link>, and{" "}
                <Link to="/dxchange">product heritage in enterprise integration</Link>.
              </p>
            </div>

            <h5 className="text-primary mt-4 mb-3">Some of our Key Milestones are:</h5>
            <table className="table table-bordered">
              <thead>
                <tr className="bg-light">
                  <th>Year</th>
                  <th>Milestones</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>1991</td>
                  <td>Memory driven simple accounting system</td>
                </tr>
                <tr>
                  <td>1991</td>
                  <td>ERP system for Transport Sector and Textile Process houses</td>
                </tr>
                <tr>
                  <td>1995</td>
                  <td>First offshore project done completely via CompuServe and Internet</td>
                </tr>
                <tr>
                  <td>1996</td>
                  <td>Changed Business Model to 100% Offshore</td>
                </tr>
                <tr>
                  <td>2000</td>
                  <td>
                    AI Expert System for Healthcare Industry in six different international
                    languages
                  </td>
                </tr>
                <tr>
                  <td>2001</td>
                  <td>
                    Developed a full rendering engine technology, which allows for fully controlled
                    rendering on any browser. Works in ASP, .Net, J2EE
                    <br />
                    Database driven Work Flow engine with Business Rules.
                    <br />
                    First use of XML HTTP (now known as AJAX) in websites
                  </td>
                </tr>
                <tr>
                  <td>2002</td>
                  <td>Designed a fully AJAX driven Internet based WYSIWYG Website Wizard in ASP</td>
                </tr>
                <tr>
                  <td>2003</td>
                  <td>
                    Implemented self-service Agent Portal for the Independent Agent Distribution
                    Channel
                  </td>
                </tr>
                <tr>
                  <td>2005</td>
                  <td>
                    Introduced Database driven CMS portal
                    <br />
                    Released AJAX event driven platform agnostic RAD Application architecture for
                    multi-lingual Internet applications and used in multiple projects
                    <br />
                    Implemented LCS (Licensing & Contracting System) for the Insurance vertical
                  </td>
                </tr>
                <tr>
                  <td>2006</td>
                  <td>
                    Architected & Implemented NEBUPRO (New Business Processing)
                    <br />
                    First version of DXchange
                  </td>
                </tr>
                <tr>
                  <td>2007</td>
                  <td>
                    Introduced Portal Builder Solution for Destination / Associations / Tourism
                    Boards
                    <br />
                    Introduced Online Bus Reservation System with integrated Agents Commission
                    management
                    <br />
                    Authored a SOA architecture based fully flexible ESB with an integrated
                    web-interface rule engine for which we were Recognized as "Top 100 IT Innovator"
                    in India by NASSCOM
                  </td>
                </tr>
                <tr>
                  <td>2008</td>
                  <td>
                    Introduced Booking Engines & Central Reservation Systems for Travel industry
                    <br />
                    Content Management systems for online portals
                  </td>
                </tr>
                <tr>
                  <td>2009</td>
                  <td>
                    Version-2 of DXchange with GUI driven Business Rules Engine for the travel
                    industry
                    <br />
                    NetPhantomDX middleware solution for BFSI industry with GUI editor
                    <br />
                    Introduced B2B, B2C, B2B2C Online Travel Portal solutions
                    <br />
                    Invited to bid for Portal Builder by a USD 28B enterprise Travel Company
                    <br />
                    Our major bus solutions customer in India was able to effect a selloff of the
                    business
                  </td>
                </tr>
                <tr>
                  <td>2010</td>
                  <td>
                    Introduced Solutions Package for B2B Travel Aggregator – includes solutions like
                    CMS, Booking Engine, CRS, XML platform and Agents Management.
                    <br />
                    Introduced n-level hierarchical Agency Management System with Commissions
                    management
                  </td>
                </tr>
                <tr>
                  <td>2011</td>
                  <td>
                    Development and Implementation of the Travel Cloud
                    <br />
                    Awarded as "Top 8 SaaS-Apps" by NASSCOM
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="shadow-sm p-3 mt-4">
              <h4 className="text-primary mb-3 font-weight-bold">What the timeline proves</h4>
              <p>
                The milestone record shows more than longevity. It shows a company that moved from
                foundational systems to offshore execution, from rule engines and portals to
                product platforms, and from early expert systems to AI-aware thinking. That is the
                background behind the company's current modernization posture.
              </p>
              <p className="mb-0">
                To see how this experience translates into current enterprise value, review the
                <Link to="/management"> leadership perspective</Link>, the{" "}
                <Link to="/advantage">enterprise advantage story</Link>, and the{" "}
                <Link to="/value-proposition">value proposition</Link> that connects this history
                to modernization outcomes. For additional proof, see the{" "}
                <Link to="/international-projects">international projects</Link> and{" "}
                <Link to="/domestic-projects">domestic projects</Link> pages.
              </p>
            </div>

            <div className="shadow-sm p-3 mt-4">
              <h4 className="text-primary mb-3 font-weight-bold">Founder authority</h4>
              <p>
                The company’s public archive identifies the business as co-founded by Lomesh Shah
                and Saurabh Mehta. That founder history supports the long-term engineering and
                enterprise delivery story already visible across the site.
              </p>
              <p className="mb-0">
                Saurabh Mehta is also referenced in the company archive as CEO and Technical
                Visionary for TravelCarma, which helps anchor the broader product and innovation
                narrative. See the{" "}
                <Link to="/news">news archive</Link> for that historical context and the{" "}
                <Link to="/management">management page</Link> for delivery governance.
              </p>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default About;
